import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectFormPage } from './pages/ProjectFormPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { CategoriesPage } from './pages/CategoriesPage';
import { UsersPage } from './pages/UsersPage';
import { LogsPage } from './pages/LogsPage';
import { ProfilePage } from './pages/ProfilePage';
import { InvitationsPage } from './pages/InvitationsPage';
import { InvitationDetailPage } from './pages/InvitationDetailPage';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-imperial"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const LeaderRoute = ({ children }) => {
  const { user, loading, isLeader } = useAuth();
  
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-imperial"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" replace />;
  if (!isLeader()) return <Navigate to="/profile" replace />;
  
  return children;
};

const ClientRedirectIndex = () => {
  const { isLeader, loading, clientAccessLoaded, getClientSingleProjectId, getClientSingleInvitationId } = useAuth();
  
  if (loading || !clientAccessLoaded) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-imperial"></div>
      </div>
    );
  }
  
  if (isLeader()) return <Navigate to="/dashboard" replace />;
  
  // Client: redirect to their first available resource
  const projectId = getClientSingleProjectId();
  if (projectId) return <Navigate to={`/projects/${projectId}`} replace />;
  
  const invitationId = getClientSingleInvitationId();
  if (invitationId) return <Navigate to={`/invitations/${invitationId}`} replace />;
  
  // Fallback for clients with no assigned resources
  return <Navigate to="/profile" replace />;
};

const ClientProjectsGuard = () => {
  const { isLeader, loading, getClientSingleProjectId } = useAuth();
  
  if (loading) return null;
  
  // Leaders see the full list
  if (isLeader()) return <ProjectsPage />;
  
  // Clients: redirect to their specific project
  const projectId = getClientSingleProjectId();
  if (projectId) return <Navigate to={`/projects/${projectId}`} replace />;
  
  // Client with multiple projects — show list
  return <ProjectsPage />;
};

const ClientInvitationsGuard = () => {
  const { isLeader, loading, getClientSingleInvitationId } = useAuth();
  
  if (loading) return null;
  
  if (isLeader()) return <InvitationsPage />;
  
  const invitationId = getClientSingleInvitationId();
  if (invitationId) return <Navigate to={`/invitations/${invitationId}`} replace />;
  
  return <InvitationsPage />;
};

const PublicRoute = ({ children }) => {
  const { user, loading, isLeader } = useAuth();
  
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-imperial"></div>
      </div>
    );
  }
  
  if (user) {
    if (isLeader()) return <Navigate to="/dashboard" replace />;
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<ClientRedirectIndex />} />
        <Route path="dashboard" element={<LeaderRoute><DashboardPage /></LeaderRoute>} />
        <Route path="projects" element={<ClientProjectsGuard />} />
        <Route path="projects/new" element={<LeaderRoute><ProjectFormPage /></LeaderRoute>} />
        <Route path="projects/:id" element={<ProjectDetailPage />} />
        <Route path="projects/:id/edit" element={<LeaderRoute><ProjectFormPage /></LeaderRoute>} />
        <Route path="invitations" element={<ClientInvitationsGuard />} />
        <Route path="invitations/:id" element={<InvitationDetailPage />} />
        <Route path="categories" element={<LeaderRoute><CategoriesPage /></LeaderRoute>} />
        <Route path="users" element={<LeaderRoute><UsersPage /></LeaderRoute>} />
        <Route path="logs" element={<LeaderRoute><LogsPage /></LeaderRoute>} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
